/**
 * AiToolCard — tool tile on the AI Tools portal landing (X.3.1 → X.6.4 → B1).
 *
 * "Warm" card: an icon tile top-left, a Playfair title, and a footer divider
 * with a right-aligned "Open →". B1 (operator, 2026-06-08) restores the
 * per-tool icon (a distinct Lucide glyph from AiToolMeta, NOT the dove on
 * every card) and a one-line description under the title. A coming-soon tool
 * (none are surfaced on the landing today) shows a muted "Coming soon" tag, a
 * de-emphasized chip, and is inert (a div, not a link). x.6.1 tokens
 * throughout; zero raw hexes.
 *
 * The icon tile follows THE ONE DL5 icon-tile spec (src/index.css):
 * rounded-card on FOREST (sage-800, matching the section band) with a
 * cream glyph — founder walk 2026-07-06 (was mid-sage/sage-400).
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { AiToolMeta } from '@/pages/AiTools';

export interface AiToolCardProps {
  tool: AiToolMeta;
  tenantSlug: string;
}

const CARD_BASE =
  'relative block rounded-card border border-transparent bg-paper px-[22px] pt-[22px] pb-[18px] shadow-sh2';
const CARD_LINK =
  `group ${CARD_BASE} text-inherit no-underline transition-[box-shadow,transform] duration-[var(--dur-slow)] ease-quiet hover:-translate-y-[3px] hover:shadow-sh3 motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400`;

export function AiToolCard({ tool, tenantSlug }: AiToolCardProps): ReactNode {
  const available = tool.status === 'available';
  const Icon = tool.icon;

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span
          data-testid={`ai-tool-chip-${tool.slug}`}
          className={
            'flex h-12 w-12 items-center justify-center rounded-card shadow-sh1 ' +
            (available ? 'bg-sage-800' : 'bg-sand')
          }
        >
          <Icon
            size={24}
            aria-hidden="true"
            className={available ? 'text-cream' : 'text-sage-300'}
          />
        </span>
        {!available && (
          <span
            data-testid={`ai-tool-status-${tool.slug}`}
            className="rounded-pill bg-sand px-2.5 py-1 text-[11px] font-semibold text-sage-400"
          >
            Coming soon
          </span>
        )}
      </div>
      <h3 className="mt-4 font-display text-[19px] font-semibold tracking-[-0.01em] text-sage-800">
        {tool.title}
      </h3>
      <p className="mt-1.5 text-[13.5px] leading-snug text-ink-soft">{tool.description}</p>
      {tool.spiceLine && (
        <p
          className="mt-1 text-title-sm leading-snug text-ink-soft"
          data-testid={`ai-tool-spice-line-${tool.slug}`}
        >
          {tool.spiceLine}
        </p>
      )}
      <div className="mt-4 flex items-center justify-end border-t border-sage-800/[6%] pt-3.5">
        <span
          className={
            'inline-flex items-center gap-1.5 text-[13px] font-semibold ' +
            (available ? 'text-sage-400' : 'text-sage-300')
          }
        >
          {available ? 'Open' : 'Soon'}
          <ArrowRight size={15} aria-hidden="true" />
        </span>
      </div>
    </>
  );

  if (!available) {
    // Coming-soon: inert (no link), de-emphasized.
    return (
      <div data-testid={`ai-tool-card-${tool.slug}`} className={`${CARD_BASE} opacity-90`}>
        {body}
      </div>
    );
  }
  return (
    <Link
      to={`/t/${tenantSlug}/ai-tools/${tool.slug}`}
      data-testid={`ai-tool-card-${tool.slug}`}
      className={CARD_LINK}
    >
      {body}
    </Link>
  );
}
